import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * VENOA Archive: Kaufbereich der Archive Edition.
 *
 * - Duft-Karten sind echte Radio-Buttons (Tastatur, Screenreader, ohne JS
 *   bedienbar). Beim Wechsel aktualisieren sich Variant-ID, Preis,
 *   Bestandszeile, Bestandsstriche, Mengen-Obergrenze, Button und Bildsatz.
 * - Alle Zahlen kommen aus Shopify (Liquid schreibt sie als JSON in die Seite).
 *   Es gibt keine Timer, keine Zähler und keine erfundenen Werte.
 * - Warenkorb: dieselbe Ajax-Anbindung wie der VENOA-Bundle-Umschalter –
 *   /cart/add.js, danach das theme-eigene CartLinesUpdateEvent (Dwell 4.1.5
 *   nutzt dafür Shopifys Storefront-Standard-Events), damit Drawer, Icon und
 *   Zähler exakt wie beim nativen Produktformular reagieren. Ohne JS schickt das
 *   Formular ganz normal an /cart/add.
 * - Keine Bundle- oder Rabattlogik: Es wird genau eine Position mit der
 *   gewählten Menge gelegt.
 */

const ADDED_RESET_MS = 2600;
const ERROR_DISPLAY_MS = 9000;
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );

class VenoaArchiveProduct extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    try {
      this.variants = JSON.parse(this.querySelector('[data-archive-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('[venoa-archive] variant data unreadable', error);
      this.variants = [];
    }

    this.form = this.querySelector('form[data-archive-form]');
    this.idInput = this.querySelector('[data-archive-id]');
    this.choices = Array.from(this.querySelectorAll('[data-archive-choice]'));
    this.sets = Array.from(this.querySelectorAll('[data-archive-set]'));
    this.priceEl = this.querySelector('[data-archive-price]');
    this.stockEl = this.querySelector('[data-archive-stock]');
    this.stockTextEl = this.querySelector('[data-archive-stock-text]');
    this.qtyInput = this.querySelector('[data-archive-qty-input]');
    this.qtyMinus = this.querySelector('[data-archive-qty-minus]');
    this.qtyPlus = this.querySelector('[data-archive-qty-plus]');
    this.addButton = this.querySelector('[data-archive-add]');
    this.addLabel = this.querySelector('[data-archive-add-label]');
    this.errorEl = this.querySelector('[data-archive-error]');
    this.statusEl = this.querySelector('[data-archive-status]');
    this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    this.busy = false;
    this.currentId = null;

    this.choices.forEach((input) => {
      const id = Number(input.value);
      input.addEventListener(
        'change',
        () => {
          if (input.checked) this.select(id);
        },
        { signal }
      );
      // Load the next scent's first image before it is chosen, so the switch
      // does not flash an empty frame.
      const label = this.querySelector(`[data-archive-choice-label="${input.value}"]`);
      label?.addEventListener('pointerenter', () => this.preload(id), { signal, once: true });
      input.addEventListener('focus', () => this.preload(id), { signal, once: true });
    });

    this.addEventListener(
      'click',
      (event) => {
        const thumb = event.target instanceof Element ? event.target.closest('[data-archive-thumb]') : null;
        if (!thumb) return;
        this.showSlide(thumb.closest('[data-archive-set]'), Number(thumb.dataset.archiveThumb));
      },
      { signal }
    );

    this.qtyMinus?.addEventListener('click', () => this.stepQuantity(-1), { signal });
    this.qtyPlus?.addEventListener('click', () => this.stepQuantity(1), { signal });
    this.qtyInput?.addEventListener('change', () => this.clampQuantity(), { signal });
    this.form?.addEventListener('submit', (event) => this.add(event), { signal });

    const checked = this.choices.find((input) => input.checked);
    this.select(Number(checked?.value || this.idInput?.value), { initial: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.addedTimeout);
    clearTimeout(this.errorTimeout);
  }

  get current() {
    return this.variants.find((variant) => variant.id === this.currentId);
  }

  /** @param {number} id @param {{initial?: boolean}} [options] */
  select(id, { initial = false } = {}) {
    const variant = this.variants.find((item) => item.id === id);
    if (!variant) return;

    const changed = this.currentId !== null && this.currentId !== id;
    this.currentId = id;
    if (this.idInput) this.idInput.value = String(id);

    this.renderPrice(variant);
    this.renderStock(variant);
    this.clampQuantity();
    this.renderButton(variant);
    this.showSet(id, changed);
    this.hideError();

    if (!initial) {
      // Same behaviour as Shopify's own variant picker. searchParams keeps every
      // other parameter (e.g. the preview key of a draft preview) untouched.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(id));
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* URL update is a convenience only. */
      }
    }
  }

  renderPrice(variant) {
    if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);
  }

  renderStock(variant) {
    if (!this.stockTextEl) return;

    const name = escapeHtml(variant.name);
    let html;
    if (!variant.available) {
      html = escapeHtml(this.dataset.textSoldOut).replaceAll('[duft]', name);
    } else if (!variant.tracked) {
      html = escapeHtml(this.dataset.textAvailable);
    } else if (variant.quantity === 1) {
      html = escapeHtml(this.dataset.textStockOne).replaceAll('[duft]', name);
    } else {
      html = escapeHtml(this.dataset.textStock)
        .replaceAll('[duft]', name)
        .replaceAll('[anzahl]', `<strong>${Number(variant.quantity)}</strong>`);
    }
    this.stockTextEl.innerHTML = html;

    let meter = this.stockEl?.querySelector('[data-archive-meter]');
    if (!variant.tracked) {
      if (meter) meter.hidden = true;
      return;
    }
    if (!meter && this.stockEl) {
      meter = document.createElement('span');
      meter.className = 'venoa-archive-meter';
      meter.setAttribute('aria-hidden', 'true');
      meter.dataset.archiveMeter = '';
      meter.dataset.max = '24';
      this.stockEl.append(meter);
    }
    if (!meter) return;

    const max = Number(meter.dataset.max) || 24;
    const quantity = Math.max(0, Number(variant.quantity) || 0);
    const ticks = Array.from({ length: Math.min(quantity, max) }, (_, index) => {
      const tick = document.createElement('span');
      tick.className = 'venoa-archive-meter__tick';
      tick.style.setProperty('--i', String(index));
      return tick;
    });
    if (quantity > max) {
      const more = document.createElement('span');
      more.className = 'venoa-archive-meter__more';
      more.textContent = '+';
      ticks.push(more);
    }
    meter.hidden = false;
    meter.classList.toggle('is-empty', quantity === 0);
    meter.replaceChildren(...ticks);
  }

  renderButton(variant) {
    if (!this.addButton) return;
    const soldOut = !variant.available;
    this.addButton.disabled = soldOut || this.busy;
    if (this.addLabel && !this.addButton.classList.contains('is-added')) {
      this.addLabel.textContent = soldOut ? this.dataset.textButtonSoldOut || '' : this.dataset.textAdd || '';
    }
  }

  /* ---------- Menge ---------- */

  maxQuantity() {
    const variant = this.current;
    if (!variant) return 1;
    return variant.tracked ? Math.max(1, Number(variant.quantity) || 1) : 99;
  }

  clampQuantity() {
    if (!this.qtyInput || this.qtyInput.type === 'hidden') return;
    const max = this.maxQuantity();
    let value = Number.parseInt(this.qtyInput.value, 10);
    if (!Number.isFinite(value) || value < 1) value = 1;
    if (value > max) value = max;
    this.qtyInput.value = String(value);
    this.qtyInput.max = String(max);
    if (this.qtyMinus) this.qtyMinus.disabled = value <= 1;
    if (this.qtyPlus) this.qtyPlus.disabled = value >= max;
  }

  stepQuantity(delta) {
    if (!this.qtyInput) return;
    const value = Number.parseInt(this.qtyInput.value, 10) || 1;
    this.qtyInput.value = String(value + delta);
    this.clampQuantity();
  }

  /* ---------- Galerie ---------- */

  showSet(id, animate) {
    this.sets.forEach((set) => {
      const active = Number(set.dataset.archiveSet) === id;
      set.hidden = !active;
      if (!active) return;
      if (animate && !reduceMotion()) {
        set.classList.remove('is-entering');
        // Restart the entrance animation on every switch.
        void set.offsetWidth;
        set.classList.add('is-entering');
      }
    });
  }

  preload(id) {
    const set = this.sets.find((item) => Number(item.dataset.archiveSet) === id);
    const image = set?.querySelector('[data-archive-slide] img');
    if (image && image.loading === 'lazy') image.loading = 'eager';
  }

  showSlide(set, index) {
    if (!set) return;
    set.querySelectorAll('[data-archive-slide]').forEach((slide) => {
      slide.hidden = Number(slide.getAttribute('data-archive-slide')) !== index;
    });
    set.querySelectorAll('[data-archive-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-pressed', Number(thumb.getAttribute('data-archive-thumb')) === index ? 'true' : 'false');
    });
  }

  /* ---------- Warenkorb ---------- */

  async add(event) {
    event.preventDefault();
    if (this.busy) return;

    const variant = this.current;
    if (!variant || !variant.available) return;

    this.clampQuantity();
    const quantity = Number.parseInt(this.qtyInput?.value || '1', 10) || 1;

    const item = { id: variant.id, quantity };
    const propertyInput = this.querySelector('[data-archive-property]');
    const propertyName = propertyInput?.getAttribute('name')?.match(/^properties\[(.+)\]$/)?.[1];
    if (propertyInput && propertyName) item.properties = { [propertyName]: propertyInput.value };

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const root = window.Shopify?.routes?.root || '/';
    const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add.js`;

    this.setBusy(true);
    this.hideError();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.reject(new Error(result.message || 'Add to cart failed'));
        this.showError(result.description || result.message);
        return;
      }

      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'venoa-archive-product',
          sourceId: this.id.toString(),
          itemCount: quantity,
          productId: this.dataset.productId,
          variantId: String(variant.id),
          sections: result.sections,
          didError: false,
        },
      });
      this.showAdded(variant, quantity);
    } catch (error) {
      console.error('[venoa-archive] add to cart failed', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.showError();
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.busy = busy;
    if (!this.addButton) return;
    this.addButton.dataset.loading = busy ? 'true' : 'false';
    this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    const variant = this.current;
    if (variant) this.renderButton(variant);
  }

  showAdded(variant, quantity) {
    if (this.statusEl) {
      this.statusEl.textContent = `${quantity} × ${variant.name} (${this.dataset.textAdded || ''})`;
    }
    if (!this.addButton || !this.addLabel) return;
    this.addButton.classList.add('is-added');
    this.addLabel.textContent = this.dataset.textAdded || this.dataset.textAdd || '';
    clearTimeout(this.addedTimeout);
    this.addedTimeout = setTimeout(() => {
      this.addButton.classList.remove('is-added');
      const current = this.current;
      if (current) this.renderButton(current);
    }, ADDED_RESET_MS);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.dataset.textError || '';
    this.errorEl.hidden = false;
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => this.hideError(), ERROR_DISPLAY_MS);
  }

  hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }

  /* ---------- Hilfen ---------- */

  formatMoney(cents) {
    const amount = Number(cents) / 100;
    const withDelimiters = (value, decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return match;
      }
    });
  }
}

if (!customElements.get('venoa-archive-product')) {
  customElements.define('venoa-archive-product', VenoaArchiveProduct);
}
